import { useCallback, useEffect, useState } from "react";
import { Flex, NumberInput } from "@mantine/core";
import styled from "styled-components";
import { useNavigate } from "react-router";
import Container from "../components/common/Container";
import PageHeader from "../components/common/PageHeader";
import CustomSelect from "../components/common/Inputs/CustomSelect";
import CustomDatePicker from "../components/common/Inputs/CustomDatePicker";
import RotationMatrix from "../components/Rotation/RotationMatrix";
import { getDepartmentSelectData } from "../api/departmentApi";
import {
  RotationPreview,
  ShiftRotation,
  getRotationPreview,
  getShiftRotations,
} from "../api/shiftRotationApi";
import { toDayKey, todayKey } from "../utils/shiftPattern";

/**
 * "BU HAFTA KİM NEREDE" (plan 2.34) — İK'nın günlük ekranı.
 *
 * NEDEN ŞART: rota HESAPLANAN bir şey. Hiçbir yerde "A ekibi 24 Ağustos'ta
 * gece" diye bir kayıt yok, dolayısıyla İK'nın rotanın doğru kurulduğunu
 * anlamasının başka yolu da yok. Kayıt olmadığında ekran tek doğrulama yolu.
 *
 * Rota LİSTESİ'nden ayrı bir sayfa: orası kurma/bakım ekranı (admin), burası
 * bakma ekranı ve bütün İK kullanıcılarına açık.
 */
const RotationBoard = () => {
  const navigate = useNavigate();

  const [departmentOptions, setDepartmentOptions] = useState<any[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
  const [rotations, setRotations] = useState<ShiftRotation[]>([]);
  const [selectedRotation, setSelectedRotation] = useState<string | null>(null);

  const [from, setFrom] = useState<string | null>(todayKey());
  const [periods, setPeriods] = useState<number>(6);
  const [preview, setPreview] = useState<RotationPreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getDepartmentSelectData()
      .then(({ data }: any) => setDepartmentOptions(data?.data ?? []))
      .catch(() => setDepartmentOptions([]));
  }, []);

  // Departman seçimi rota listesini süzüyor; seçim yoksa şirketteki bütün
  // yürürlükteki rotalar geliyor — küçük şirkette departman seçmeye zorlamak
  // gereksiz bir adım olurdu.
  const loadRotations = useCallback(async () => {
    try {
      const data: any = await getShiftRotations({
        limit: 50,
        departmentId: selectedDepartment ?? undefined,
      });
      const rows: ShiftRotation[] = data?.data ?? [];
      setRotations(rows);
      // Seçili rota süzgeçten düştüyse ilkine geç; tek rota varsa zaten o.
      setSelectedRotation((current) =>
        current && rows.some((r) => r._id === current)
          ? current
          : rows[0]?._id ?? null
      );
    } catch {
      setRotations([]);
      setSelectedRotation(null);
    }
  }, [selectedDepartment]);

  useEffect(() => {
    loadRotations();
  }, [loadRotations]);

  const loadPreview = useCallback(async () => {
    if (!selectedRotation) {
      setPreview(null);
      return;
    }
    try {
      const data: any = await getRotationPreview(
        selectedRotation,
        toDayKey(from) ?? undefined,
        periods
      );
      if (data?.success) {
        setPreview(data.data);
        setError(null);
      } else {
        setPreview(null);
        setError(data?.message ?? "Rota okunamadı.");
      }
    } catch {
      setPreview(null);
      setError("Rota okunamadı.");
    }
  }, [selectedRotation, from, periods]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const rotationOptions = rotations.map((r) => ({
    value: r._id,
    label: r.name,
  }));

  return (
    <Container>
      <PageHeader
        pageTitle="Bu Hafta Kim Nerede"
        subText="Rota hesaplanan bir şey; hangi ekibin hangi dönemde hangi vardiyada olduğu burada görünür."
      />

      <Flex mb={24} gap={16} align="flex-end" wrap="wrap">
        <CustomSelect
          label="Departman"
          placeholder="Tümü"
          data={departmentOptions}
          value={selectedDepartment}
          onChange={setSelectedDepartment}
          clearable
          searchable
          w={220}
        />
        <CustomSelect
          label="Rota"
          placeholder={rotations.length ? "Rota Seçiniz" : "Rota yok"}
          data={rotationOptions}
          value={selectedRotation}
          onChange={setSelectedRotation}
          searchable
          w={260}
          disabled={rotations.length === 0}
        />
        <CustomDatePicker
          label="Başlangıç"
          value={from}
          onChange={setFrom}
          clearable={false}
        />
        <NumberInput
          label="Dönem sayısı"
          w={140}
          min={1}
          max={26}
          value={periods}
          onChange={(v) => setPeriods(Math.min(26, Math.max(1, Number(v) || 1)))}
        />
      </Flex>

      {rotations.length === 0 ? (
        <EmptyState>
          {selectedDepartment
            ? "Bu departmanda yürürlükte bir rota yok."
            : "Yürürlükte bir rota yok."}{" "}
          <LinkButton onClick={() => navigate("/shifts/rotations/new")}>
            Rota kurun
          </LinkButton>{" "}
          — önce <strong>Ekipler</strong> ekranından ekipleri açmanız gerekir.
        </EmptyState>
      ) : error ? (
        <ErrorState>{error}</ErrorState>
      ) : (
        <>
          <RotationMatrix
            periods={preview?.periods ?? []}
            periodType={preview?.rotation.periodType}
          />
          <Hint>
            Bir kişinin bu tabloya girmesi için <strong>ekibe atanmış</strong>{" "}
            olması gerekir — "Personel Atama" ekranının <strong>Ekip</strong>{" "}
            kipinden yapılır. Ekibi olmayan personel rotadan beslenmez.
          </Hint>
        </>
      )}
    </Container>
  );
};

export default RotationBoard;

const EmptyState = styled.div`
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  padding: 32px;
  color: #6b7280;
  font-size: 15px;
  line-height: 22px;
`;

const ErrorState = styled.div`
  border: 1px dashed #fca5a5;
  border-radius: 6px;
  padding: 24px;
  color: #b91c1c;
  font-size: 14px;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: #5c26ff;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  text-decoration: underline;
`;

const Hint = styled.div`
  margin-top: 16px;
  color: #6b7280;
  font-size: 13px;
  line-height: 20px;
  max-width: 780px;
`;
